import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building, Users, Package, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const DHLHalleArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Industry Highlights
        </Button>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-orange-500 text-white">
                <Building className="h-3 w-3 mr-1" />
                Infrastructure
              </Badge>
              <span className="text-gray-500 text-sm">November 2025</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              DHL Supply Chain Opens State-of-the-Art Logistics Centre in Halle
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              DHL's third facility in the Leipzig-Halle region represents a major expansion in sustainable 
              logistics infrastructure, featuring cutting-edge technology and DGNB Gold certification.
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://postandparcel.info/wp-content/uploads/2025/11/DHL-Supply-Chain-Halle-2-975x640.jpg" 
              alt="Modern DHL logistics warehouse facility with sustainable design" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Building className="h-5 w-5 mr-2 text-orange-600" />
                  Facility Size
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600">34,000</div>
                <p className="text-sm text-gray-600">Square metres</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Package className="h-5 w-5 mr-2 text-blue-600" />
                  Capacity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">55,000</div>
                <p className="text-sm text-gray-600">Pallet spaces</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2 text-green-600" />
                  Employment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">450+</div>
                <p className="text-sm text-gray-600">Current employees</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Facility Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              DHL Supply Chain's new logistics centre in Halle (Saale) represents a significant investment 
              in Germany's logistics infrastructure. As the third DHL facility in the Leipzig-Halle region, 
              this state-of-the-art complex strengthens DHL's position as a leading logistics provider while 
              setting new standards for sustainable operations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Technical Specifications</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Facility Features:</h4>
              <ul className="space-y-2">
                <li><strong>Three Operational Halls:</strong> Each spanning 10,500 square metres</li>
                <li><strong>Loading Infrastructure:</strong> 27 loading docks for efficient operations</li>
                <li><strong>Storage Capacity:</strong> Up to 55,000 pallet spaces</li>
                <li><strong>Total Area:</strong> 34,000 square metres of warehouse space</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Sustainability Leadership</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The facility achieves DGNB Gold certification, demonstrating DHL's commitment to sustainable logistics:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Photovoltaic Systems:</strong> On-site renewable energy generation</li>
              <li><strong>LED Lighting:</strong> Energy-efficient illumination throughout the facility</li>
              <li><strong>Sustainable Design:</strong> Built to highest environmental standards</li>
              <li><strong>Emission Reduction:</strong> Supporting customers' sustainability goals</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategic Location Advantages</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The Halle location provides exceptional connectivity for European logistics operations. 
              Its proximity to Leipzig Airport (LEJ) enables late cut-off times of 10:30 PM for next-day 
              global deliveries, while multimodal transport links provide access to road, rail, and air networks.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Service Capabilities</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Comprehensive Logistics Solutions:</h4>
              <ul className="space-y-2">
                <li><strong>Warehousing & Picking:</strong> Including hazardous materials and batteries</li>
                <li><strong>Value-Added Services:</strong> Custom logistics solutions</li>
                <li><strong>Automated Solutions:</strong> Complex technology integration</li>
                <li><strong>Technology Sector Focus:</strong> Specialized expertise and knowledge</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Employment Impact</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The facility currently employs more than 450 people, with plans to create an additional 
              100 jobs once the extension building is fully utilized. This represents a significant 
              contribution to the local economy and demonstrates DHL's commitment to the region.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Integration with DHL Campus</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The new centre is fully integrated into the DHL Leipzig-Halle Campus, providing seamless 
              connectivity with existing operations. This integration enables efficient resource sharing, 
              optimized workflows, and enhanced service delivery for both new and existing customers.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Future Growth Potential</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              According to DHL leadership, the facility still has available space for additional customers, 
              positioning it for continued growth. The combination of strategic location, advanced technology, 
              and sustainable operations makes it an attractive option for companies seeking reliable logistics partnerships.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on official DHL Supply Chain announcement regarding the Halle logistics centre opening. 
                <a href="https://www.stattimes.com/supply-chain/dhl-supply-chain-opens-new-high-capacity-logistics-centre-in-halle-1357218" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default DHLHalleArticle;